import { Tooltip } from '@chakra-ui/react'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2Card from '@core/components/ApplyDialogV2/ApplyV2Card'
import ApplyV2InputTextarea from '@core/components/ApplyDialogV2/ApplyV2InputTextarea'
import ApplyV2NoFieldsSwitch from '@core/components/ApplyDialogV2/ApplyV2NoFieldsSwitch'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import clsx from 'clsx'
import React, { useState, useEffect, useRef } from 'react'
import './ApplyV2ReferenceStep.scss'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import {
    usePostReferenceV2ApplyMutation,
    useDeleteReferenceV2ApplyMutation,
    usePutReferenceV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'

import { ThisIcon } from './icons/icon'
import PhoneInput from '../ApplyV2ProfileStep/PhoneInput'
import { useTranslation } from 'react-i18next'

type Reference = {
    uid?: string
    name: string
    companyName: string
    title: string
    email: string
    description: string
    phonePrefix: string
    phoneNumber: string
}

const defaultReference: Reference = {
    name: '',
    companyName: '',
    title: '',
    email: '',
    description: '',
    phonePrefix: '',
    phoneNumber: '',
}

const baseClass = 'ApplyV2ReferenceStep'

const mapApiReference = (apiRef: any): Reference => ({
    uid: apiRef.uid || '',
    name: apiRef.name || '',
    companyName: apiRef.companyName || '',
    title: apiRef.title || '',
    email: apiRef.email || '',
    description: apiRef.description || '',
    phonePrefix: apiRef.mobilePhone?.prefix || '',
    phoneNumber: apiRef.mobilePhone?.number || '',
})

const ReferenceStep: React.FC = () => {
    const { t } = useTranslation()
    const { getTitle } = useApplyV2NavTranslations()
    const { postProfileData, onNextStep, postProfileRefetch, isPostProfileLoading, isPostApplied } = useApplyV2Context()
    const [postReference, { isLoading: isPostLoading }] = usePostReferenceV2ApplyMutation()
    const [putReference, { isLoading: isPutLoading }] = usePutReferenceV2ApplyMutation()
    const [deleteReference, { isLoading: isDeleteLoading }] = useDeleteReferenceV2ApplyMutation()

    const references: Reference[] = (postProfileData?.data?.reference ?? postProfileData?.data?.reference ?? []).map(
        mapApiReference
    )

    const uid = postProfileData?.data?.uid ?? postProfileData?.data?.uid

    const initialHasAny = references.length > 0
    const [state, setState] = useState<'empty' | 'list' | 'add' | 'edit'>(initialHasAny ? 'list' : 'empty')
    const [editUid, setEditUid] = useState<string | null>(null)
    const [form, setForm] = useState<Reference>(defaultReference)
    const [noReference, setNoReference] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})

    const descRef = useRef<HTMLTextAreaElement | null>(null)
    useEffect(() => {
        const el = descRef.current
        if (!el) return
        el.style.height = '80px'
        el.style.height = `${el.scrollHeight}px`
    }, [form.description])

    useEffect(() => {
        if (state === 'add' || state === 'edit') return
        setState(references.length ? 'list' : 'empty')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [references.length])

    const referenceToApiBody = (data: Reference) => ({
        ...(data.uid ? { uid: data.uid } : {}),
        companyName: data.companyName,
        email: data.email,
        name: data.name,
        title: data.title,
        description: data.description,
        mobilePhone:
            data.phonePrefix?.trim() && data.phoneNumber?.trim()
                ? { prefix: data.phonePrefix.trim(), number: data.phoneNumber.trim() }
                : null,
    })

    const isFormValid =
        form.name.trim() !== '' &&
        form.companyName.trim() !== '' &&
        form.title.trim() !== '' &&
        (form.phoneNumber.trim() !== '' || form.email.trim() !== '')

    const handleFormSubmit = async () => {
        if (!uid) return
        try {
            if (editUid) {
                await putReference({ uid, body: { ...referenceToApiBody(form), uid: editUid } }).unwrap()
            } else {
                await postReference({ uid, body: referenceToApiBody(form) }).unwrap()
            }
            setState('list')
            setEditUid(null)
            setForm(defaultReference)
            setFieldErrors({})
            postProfileRefetch()
        } catch (e: any) {
            const errFields: { [key: string]: string } = {}
            const errors = e?.data?.errors || []
            for (const err of errors) {
                const { field, msg } = err
                switch (field) {
                    case 'name':
                        errFields.name = msg || 'Required field'
                        break
                    case 'companyName':
                        errFields.companyName = msg || 'Required field'
                        break
                    case 'title':
                        errFields.title = msg || 'Required field'
                        break
                    case 'mobilePhone.number':
                        errFields.phone = msg || 'Invalid phone'
                        break
                    case 'email':
                        errFields.email = msg || 'Required field'
                        break
                    case 'description':
                        errFields.description = msg || 'Required field'
                        break
                }
            }
            setFieldErrors(errFields)
        }
    }

    const handleDeleteClick = async (idx: number) => {
        if (!uid) return
        const reference = references[idx]
        if (!reference?.uid) return
        try {
            await deleteReference({ uid, referenceUid: reference.uid }).unwrap()
            setState('list')
            postProfileRefetch()
        } catch (e) {
            console.error('Failed to delete reference', e)
        }
    }

    const handleAddClick = () => {
        setForm(defaultReference)
        setEditUid(null)
        setState('add')
    }

    const handleEditClick = (idx: number) => {
        const ref = references[idx]
        setForm(ref)
        setEditUid(ref.uid || null)
        setState('edit')
    }

    const handleFormChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = (
        e
    ) => {
        const { name, value } = e.currentTarget
        setForm((prev) => ({ ...prev, [name]: value }))
    }

    const handlePhoneInputChange = ({ prefix, number }: { prefix: string; number: string }) => {
        setForm((prev) => ({ ...prev, phonePrefix: prefix, phoneNumber: number }))
    }

    const handleCancel = () => {
        setState(references.length ? 'list' : 'empty')
        setEditUid(null)
        setForm(defaultReference)
    }

    // ---- UI ----
    return (
        <div className="ReferenceStep">
            {state === 'empty' && (
                <>
                    <div className="edu-empty">
                        <div className="edu-empty-icon">
                            <ThisIcon />
                        </div>
                        <div className="edu-empty-content">
                            <div className="edu-empty-title">
                                {noReference
                                    ? t('10305', "I don't have any reference")
                                    : t('10295', 'No references added yet')}
                            </div>
                            <div className="edu-empty-actions">
                                <button
                                    className="add-edu-btn"
                                    type="button"
                                    disabled={noReference}
                                    onClick={handleAddClick}
                                >
                                    + {t('1211', 'Add reference') as string}
                                </button>
                                {/*<div className="no-edu-toggle" onClick={() => setNoReference((v) => !v)}>*/}
                                {/*    <div className={`toggle-switch${noReference ? ' checked' : ''}`}>*/}
                                {/*        <div className="toggle-thumb" />*/}
                                {/*    </div>*/}
                                {/*    <span>{t('6178', 'No references')}</span>*/}
                                {/*</div>*/}
                                <ApplyV2NoFieldsSwitch
                                    className={`${baseClass}__empty--desc-switch`}
                                    checked={noReference}
                                    onChange={(v) => setNoReference(v)}
                                    label={getTitle('6178', 'No references')}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row right">
                        <button type="button" disabled={!noReference} className="next-btn" onClick={onNextStep}>
                            {t('3035', 'Next')}
                        </button>
                    </div>
                </>
            )}

            {(state === 'add' || state === 'edit') && (
                <div className="edu-form">
                    <div className="edu-form-header">
                        <span>{state === 'edit' ? 'Edit reference' : 'Add reference'}</span>
                        {/*<button className="close-btn" onClick={handleCancel}>*/}
                        {/*    ×*/}
                        {/*</button>*/}
                        <Tooltip
                            hasArrow
                            // isDisabled={isLoading}
                            openDelay={500}
                            label={getTitle('416', 'Cancel')}
                            // placement={'right'}
                            padding={'8px 16px'}
                            borderRadius={'4px'}
                            backgroundColor={'#19191A'}
                            fontWeight={600}
                            fontSize={'12px'}
                            shouldWrapChildren
                        >
                            <ApplyV2Button
                                className={clsx(`${baseClass}__header--buttons-btn`, {})}
                                variant={'transparent'}
                                size={'small'}
                                onClick={handleCancel}
                                leftIcon={<i className="fa-regular fa-xmark"></i>}
                            />
                        </Tooltip>
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('6745', 'Full name')}*</label>
                            <input
                                className={fieldErrors.name ? 'has-error' : ''}
                                name="name"
                                value={form.name}
                                onChange={handleFormChange}
                                maxLength={255}
                                placeholder={t('6750', 'Enter referent full name') as string}
                            />
                        </div>
                        {fieldErrors.name && <div className="field-error">{fieldErrors.name}</div>}
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('312', 'Company name')}*</label>
                            <input
                                className={fieldErrors.companyName ? 'has-error' : ''}
                                name="companyName"
                                value={form.companyName}
                                onChange={handleFormChange}
                                maxLength={255}
                                placeholder={t('714', 'Enter referent company name') as string}
                            />
                        </div>
                        {fieldErrors.companyName && <div className="field-error">{fieldErrors.companyName}</div>}
                    </div>

                    <div className=" ">
                        <div className="field">
                            <label>{t('171', 'Title')}*</label>
                            <input
                                className={fieldErrors.title ? 'has-error' : ''}
                                name="title"
                                value={form.title}
                                onChange={handleFormChange}
                                maxLength={255}
                                placeholder={t('696', 'Enter referent title') as string}
                            />
                        </div>
                        {fieldErrors.title && <div className="field-error">{fieldErrors.title}</div>}
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('137', 'Mobile phone') as string}*</label>
                            <PhoneInput
                                prefix={form.phonePrefix}
                                number={form.phoneNumber}
                                onChange={handlePhoneInputChange}
                                height={36}
                                slotStyles={{
                                    number: {
                                        height: 36,
                                        fontSize: 14,
                                        padding: '0 12px',
                                        border: '1px solid #CBCBCD',
                                        borderTopRightRadius: 6,
                                        borderBottomRightRadius: 6,
                                        outline: 'none',
                                    },
                                }}
                            />
                        </div>
                        {fieldErrors.phone && <div className="field-error">{fieldErrors.phone}</div>}
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('92', 'Email')}*</label>
                            <input
                                className={fieldErrors.email ? 'has-error' : ''}
                                name="email"
                                value={form.email}
                                onChange={handleFormChange}
                                placeholder={t('727', 'Enter referent email') as string}
                            />
                        </div>
                        {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
                    </div>

                    <div className="">
                        <div className="field full">
                            <label>{t('606', 'Description') as string}</label>
                            <textarea
                                ref={descRef}
                                className={`auto-textarea${fieldErrors.description ? ' has-error' : ''}`}
                                name="description"
                                value={form.description}
                                onChange={handleFormChange}
                                placeholder={t('1650', 'Description') as string}
                                rows={1}
                                maxLength={5000}
                            />
                            {/*<ApplyV2InputTextarea*/}
                            {/*    name="description"*/}
                            {/*    label={t('606', 'Description') ?? ''}*/}
                            {/*    placeholder={t('1650', 'Description') ?? ''}*/}
                            {/*    // rows={1}*/}
                            {/*    value={form.description}*/}
                            {/*    onChange={handleFormChange}*/}
                            {/*/>*/}
                        </div>
                        {fieldErrors.description && <div className="field-error">{fieldErrors.description}</div>}
                    </div>
                    <div className={`${baseClass}__btns`}>
                        <ApplyV2Button
                            type={'reset'}
                            variant="transparent"
                            className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                            // isLoading={formik.isSubmitting}
                            onClick={() => {
                                // formik.resetForm()
                                // onClose()
                                handleCancel()
                            }}
                        >
                            {getTitle('416', 'Cancel')}
                        </ApplyV2Button>
                        <ApplyV2Button
                            type={'submit'}
                            variant="primary"
                            className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                            // isLoading={formik.isSubmitting}
                            // isDisabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                            // isDisabled={!formik.isValid || !formik.dirty}
                            isLoading={isPostLoading || isPutLoading || isDeleteLoading}
                            onClick={handleFormSubmit}
                        >
                            {state === 'edit' ? getTitle('12', 'Save') : getTitle('179', 'Add')}
                        </ApplyV2Button>
                    </div>
                    {/*<div className="row actions">*/}
                    {/*    <button type="button" className="cancel-btn" onClick={handleCancel}>*/}
                    {/*        Cancel*/}
                    {/*    </button>*/}
                    {/*    <button*/}
                    {/*        type="button"*/}
                    {/*        className="add-btn"*/}
                    {/*        onClick={handleFormSubmit}*/}
                    {/*        disabled={!isFormValid || isPostLoading || isPutLoading}*/}
                    {/*    >*/}
                    {/*        {state === 'edit' ? 'Save' : 'Add'}*/}
                    {/*    </button>*/}
                    {/*</div>*/}
                </div>
            )}

            {state === 'list' && (
                <div>
                    <div className="edu-list edu-card">
                        {references.map((ref, i) => (
                            <ApplyV2Card
                                key={ref.uid || i}
                                className="edu-block"
                                title={decodeHtmlEntitiesRecursive(ref.name)}
                                emailSection={ref.email}
                                headerDescription={decodeHtmlEntitiesRecursive(
                                    [ref.companyName, ref.title].filter(Boolean).join(' • ')
                                )}
                                phone={{ prefix: ref.phonePrefix, number: ref.phoneNumber }}
                                onEdit={() => handleEditClick(i)}
                                onDelete={() => handleDeleteClick(i)}
                            >
                                {ref.description}
                            </ApplyV2Card>
                        ))}

                        <div className="add-edu-row">
                            {/*<button className="add-edu-btn" type="button" onClick={handleAddClick}>*/}
                            {/*    + {t('1211', 'Add reference')}*/}
                            {/*</button>*/}
                            <ApplyV2Button
                                variant={'transparent'}
                                className={`${baseClass}__addBtn`}
                                onClick={handleAddClick}
                                isLoading={isPostLoading || isPutLoading || isDeleteLoading}
                                leftIcon={
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.99967 1.33301C8.36786 1.33301 8.66634 1.63148 8.66634 1.99967V7.12611H13.9997C14.3679 7.12611 14.6663 7.42459 14.6663 7.79278C14.6663 8.16097 14.3679 8.45944 13.9997 8.45944H8.66634V13.9997C8.66634 14.3679 8.36786 14.6663 7.99967 14.6663C7.63148 14.6663 7.33301 14.3679 7.33301 13.9997V8.45944H1.99967C1.63148 8.45944 1.33301 8.16097 1.33301 7.79278C1.33301 7.42459 1.63148 7.12611 1.99967 7.12611H7.33301V1.99967C7.33301 1.63148 7.63148 1.33301 7.99967 1.33301Z"
                                            fill="#19191A"
                                        />
                                    </svg>
                                }
                            >
                                {getTitle('1533', 'Add course/certification')}
                            </ApplyV2Button>
                        </div>
                    </div>

                    <div className="row right">
                        {/*<button type="button" className="next-btn" onClick={onNextStep}>*/}
                        {/*    {t('3035', 'Next')}*/}
                        {/*</button>*/}
                        <ApplyV2Button
                            variant={'primary'}
                            className={`${baseClass}__next--btn`}
                            onClick={onNextStep}
                            isLoading={isPostApplied || isPostProfileLoading}
                            // isDisabled={isPostApplied || isPostProfileLoading || (!isNoItems && references.length === 0)}
                            isDisabled={isPostApplied || isPostProfileLoading}
                            type={'submit'}
                        >
                            {getTitle('3035', 'Next')}
                        </ApplyV2Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ReferenceStep
